import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkles, Disc3, Crown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, MaskLine } from "../components/Reveal";

const TIERS = [
  {
    name: "Free",
    price: "$0",
    period: "",
    blurb: "Everything you need to start listening together.",
    features: [
      "Join unlimited sessions",
      "Create up to 3 playlists",
      "Create profile",
      "Share via Music DM's",
    ],
    cta: "Get Started",
    featured: false,
  },
  {
    name: "Monthly",
    price: "$5.99",
    period: "/month",
    features: [
      "Join unlimited sessions",
      "Create unlimited playlists",
      "QR code joining",
      "Create profile",
      "Share via Music DM's",
      "Priority support",
    ],
    cta: "Go Premium",
    featured: true,
  },
  {
    name: "Yearly",
    price: "$59.99",
    period: "/year",
    was: "$71.88",
    features: [
      "Join unlimited sessions",
      "Create unlimited playlists",
      "QR code joining",
      "Create profile",
      "Share via Music DM's",
      "2 months free",
      "Priority support",
    ],
    cta: "Go Premium Yearly",
    featured: false,
  },
];

const PRICING_FAQS = [
  {
    q: "Can I cancel anytime?",
    a: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes. The Free plan includes access to 3 playlists. Upgrade to Premium for unlimited playlists.",
  },
  {
    q: "Do I need Apple Music?",
    a: "Yes. Tap In integrates with Apple Music. You'll need an active Apple Music subscription to access music on this platform.",
  },
];

function PricingFaqItem({ item, index, open, onToggle }) {
  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={`pricing-faq-${index}`}>
      <button
        type="button"
        data-testid={`pricing-faq-question-${index}`}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="font-semibold text-[0.95rem]">{item.q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="text-grape-soft flex-none">
          <Plus size={18} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-[0.92rem] leading-relaxed text-dusk">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState(0);
  return (
    <>
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-12 text-center" data-testid="pricing-hero">
        <div className="glow-blob" style={{ width: 560, height: 560, top: "-26%", left: "50%", transform: "translateX(-50%)" }} aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <h1 className="hero-title !text-[clamp(2rem,4.6vw,3.4rem)]" data-testid="pricing-title">
            <MaskLine delay={0.05}>
              Simple <span className="text-gradient">Pricing</span>
            </MaskLine>
          </h1>
          <p className="body-lg max-w-xl mx-auto mt-6">Start free. Upgrade when ready.</p>
        </div>
      </section>

      <section className="pb-8" data-testid="pricing-tiers">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-3 gap-5 items-stretch">
          {TIERS.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.07} className="h-full" testid={`tier-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <article className={`bento-card h-full ${tier.featured ? "pricing-featured !border-[#D4AF37]/70 shadow-[0_0_44px_rgba(212,175,55,0.16)]" : ""}`}>
                {tier.featured && (
                  <span className="pricing-gold-chip absolute top-5 right-5 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/60 bg-[#D4AF37]/10 rounded-full px-3 py-1">
                    Most Popular
                  </span>
                )}
                <h3 className="font-display text-lg flex items-center gap-2">
                  {tier.name}
                  {tier.featured && <Crown size={17} className="pricing-gold-crown text-[#D4AF37]" aria-label="Premium" />}
                </h3>
                <p className="flex items-end gap-2 mt-4">
                  <span className="font-display text-4xl text-gradient">{tier.price}</span>
                  {tier.period && <span className="text-sm text-dusk pb-1.5">{tier.period}</span>}
                  {tier.was && <span className="text-sm text-dusk pb-1.5 line-through">{tier.was}</span>}
                </p>
                {tier.blurb && <p className="body-lg mt-4 text-[0.9rem]">{tier.blurb}</p>}
                <ul className="mt-6 space-y-2.5 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-chalk/85">
                      <Disc3 size={15} className="text-chalk mt-0.5 flex-none" />
                      {f}
                    </li>
                  ))}
                </ul>
                {tier.featured || tier.name === "Yearly" ? (
                  <button
                    type="button"
                    data-testid={`tier-cta-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`${tier.featured ? "btn-primary" : "btn-ghost"} mt-8 w-full justify-center cursor-pointer`}
                  >
                    {tier.cta}
                  </button>
                ) : (
                  <Link
                    to="/access"
                    data-testid={`tier-cta-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`${tier.featured ? "btn-primary" : "btn-ghost"} mt-8 w-full justify-center`}
                  >
                    {tier.cta}
                  </Link>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section !pt-10" data-testid="pricing-faq">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="eyebrow">Pricing FAQ</p>
            <h2 className="section-title mt-4">
              Frequently asked <span className="text-gradient">questions</span>
            </h2>
          </Reveal>
          <div className="mt-10 space-y-3">
            {PRICING_FAQS.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.05}>
                <PricingFaqItem
                  item={item}
                  index={i}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
